'use client';

/**
 * Persisted stale-while-revalidate cache for public-menu overlays.
 *
 * THE PROBLEM
 *
 * Every sheet and dialog on the public menu is behind a Radix `Sheet`/`Dialog`,
 * which UNMOUNTS its children when closed. So each one used to cold-start on
 * open: mount, fire a fetch, show a spinner, render. The guest paid for that
 * wait on every visit, for data that had not changed in the last sixty seconds.
 *
 * The first fix was `lib/customer/account-prefetch.ts` — warm the data while the
 * guest reads the menu. That removed the wait within one page visit, and it is
 * the right shape. But it kept its cache in a module-level `Map`, and a `Map`
 * dies with the document. So a RELOAD, which is exactly what a guest does after
 * scanning a fresh QR code or switching tabs to check the total, put every
 * overlay straight back to a spinner. In-memory was half the fix.
 *
 * THE SHAPE OF THE FIX
 *
 * localStorage underneath, a `Map` on top. The `Map` is the hot path — a guest
 * opening and closing the same sheet ten times in a minute never touches
 * storage. The `localStorage` copy is what survives a reload, so the first paint
 * after a reload is synchronous and instant.
 *
 * Reads are STALE-WHILE-REVALIDATE, not cache-until-expiry. `peekPersisted`
 * returns a possibly-stale value immediately and the caller refreshes behind it,
 * because the failure mode we are avoiding is a spinner, and a spinner is much
 * worse than a number that is 30 seconds old and self-correcting. A guest
 * watching their own order total tick over is fine; a guest watching an empty
 * sheet fill in is not.
 *
 * WHY NOT A REAL OFFLINE CACHE
 *
 * Nothing here promises offline. These overlays are the checkout path; serving
 * a genuinely stale price or availability list from a cache with no revalidation
 * would be a money bug, and that is a different (much larger) piece of work with
 * a different threat model. This is a latency cache that always revalidates.
 *
 * Destructive operations are the caller's job: anything that changes a cart, a
 * reservation or a saved address must call `invalidatePersisted` for the keys it
 * touched, or the guest will read their own stale edit back at them.
 */

const NAMESPACE = 'menuraq:menu-cache';

/** Kept in a module Map so repeat opens never touch storage. */
const hot = new Map<string, string>();

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    // Private-mode Safari and hardened browsers throw on access, not just on
    // write, so the very first line has to be inside the try.
    const probe = window.localStorage;
    return probe;
  } catch {
    return null;
  }
}

function fullKey(key: string) {
  return `${NAMESPACE}:${key}`;
}

/**
 * Read a value regardless of age.
 *
 * The caller decides what to do with a stale one — that decision is the whole
 * point of SWR and it differs per caller (a guest total wants a background
 * refresh, a promo banner is fine as-is until the next full load).
 */
export function peekPersisted<T>(key: string): { value: T; at: number } | null {
  const full = fullKey(key);
  const raw = hot.get(full) ?? storage()?.getItem(full) ?? null;
  if (raw == null) return null;

  // Promote to the hot path even on a hit, so the next read is a Map lookup.
  hot.set(full, raw);

  try {
    const parsed = JSON.parse(raw) as { v: unknown; at: number };
    if (!parsed || typeof parsed.at !== 'number') return null;
    return { value: parsed.v as T, at: parsed.at };
  } catch {
    // A corrupt entry is worse than a missing one: it would throw on every read
    // for as long as it stayed in storage. Drop it and behave as if absent.
    invalidatePersisted(key);
    return null;
  }
}

/** Read only if younger than `maxAgeMs`. */
export function readPersisted<T>(key: string, maxAgeMs: number): T | null {
  const hit = peekPersisted<T>(key);
  if (!hit) return null;
  return Date.now() - hit.at <= maxAgeMs ? hit.value : null;
}

export function writePersisted<T>(key: string, value: T) {
  const payload = JSON.stringify({ v: value, at: Date.now() });
  hot.set(fullKey(key), payload);
  try {
    storage()?.setItem(fullKey(key), payload);
  } catch {
    // Quota exceeded, or storage disabled. The `Map` copy still serves this page
    // view, which is the common case; the reload penalty is an acceptable
    // degradation and must never break the render.
  }
}

export function invalidatePersisted(key: string) {
  hot.delete(fullKey(key));
  try {
    storage()?.removeItem(fullKey(key));
  } catch {
    // Nothing to do — the hot copy is already gone, which is what the caller
    // is about to read.
  }
}

/** Drop everything, or one prefix. Used on sign-out and on menu switch. */
export function invalidatePersistedPrefix(prefix: string) {
  for (const key of [...hot.keys()]) {
    if (key.startsWith(fullKey(prefix))) hot.delete(key);
  }
  const store = storage();
  if (!store) return;
  try {
    // Scoped to our namespace, so this can never touch another feature's keys.
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(fullKey(prefix))) doomed.push(key);
    }
    doomed.forEach((key) => store.removeItem(key));
  } catch {
    // Best effort.
  }
}

/**
 * Deduplicates concurrent identical loads, and carries the revalidation across.
 *
 * Two copies of the same overlay mounting in one tick is the norm here, not an
 * edge case — the cart sheet and the order-success dialog each mount their own
 * loyalty panel, and both mount in the same tick. Without this, opening the cart
 * fired two identical requests and the second one could overwrite a fresher
 * first answer with a slower stale one.
 */
const inFlight = new Map<string, Promise<unknown>>();

export function loadPersisted<T>(key: string, loader: () => Promise<T>): Promise<T | null> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const request = (async () => {
    try {
      const value = await loader();
      // A `null`/`undefined` result is a FAILURE, not a value. Caching it would
      // make "this request failed once" look like "there is genuinely nothing
      // here" for the whole TTL, and an empty sheet is exactly the dead end the
      // cache exists to prevent. The previous good value stays in place.
      if (value != null) writePersisted(key, value);
      return value;
    } catch {
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}
